import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { usePatient } from "@/context/PatientContext";
import { api } from "@/lib/api";
import { Pill, CalendarBlank, Bell, CheckSquare, Question, Warning, Plus, ClockCounterClockwise, FileText } from "@phosphor-icons/react";
import InvitesBanner from "@/components/InvitesBanner";
import TimelineFeed from "@/components/TimelineFeed";

function StatCard({ icon: Icon, label, value, tone = "primary", testid }) {
  const tones = {
    primary: "bg-[#2C5545]/8 text-[#2C5545]",
    warning: "bg-[#D49A4C]/15 text-[#B37927]",
    secondary: "bg-[#C86A51]/12 text-[#B05A43]",
  };
  return (
    <div data-testid={testid} className="bg-white rounded-2xl border border-[#EBE8E0] p-6 hover:shadow-md transition-shadow">
      <div className={`w-11 h-11 rounded-full ${tones[tone]} flex items-center justify-center`}>
        <Icon size={22} weight="duotone" />
      </div>
      <div className="mt-4 text-3xl font-heading font-bold">{value}</div>
      <div className="text-sm text-[#576A62] mt-1">{label}</div>
    </div>
  );
}

export default function Dashboard() {
  const { activePatient, patients } = usePatient();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!activePatient) return;
    setLoading(true);
    api.get(`/dashboard/${activePatient.id}`)
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [activePatient]);

  if (patients.length === 0) {
    return (
      <>
        <InvitesBanner />
        <div className="rounded-3xl overflow-hidden border border-[#EBE8E0] bg-white">
        <div className="grid md:grid-cols-2">
          <div className="p-10">
            <div className="text-xs uppercase tracking-[0.2em] text-[#576A62] font-semibold">Get started</div>
            <h1 className="mt-3 font-heading text-3xl sm:text-4xl font-bold">Add your first patient</h1>
            <p className="mt-3 text-[#576A62] leading-relaxed">
              A "patient" is anyone you care for — a parent, a partner, or yourself.
              You can add more later and switch between them from the top bar.
            </p>
            <Link
              to="/app/patients?new=1"
              data-testid="dashboard-empty-add-patient"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#2C5545] hover:bg-[#224235] text-white px-6 py-3 font-semibold transition-colors"
            >
              <Plus weight="bold" size={16} /> Add patient
            </Link>
          </div>
          <img
            src="https://images.pexels.com/photos/18509794/pexels-photo-18509794.jpeg"
            alt=""
            className="h-full w-full object-cover"
          />
        </div>
      </div>
      </>
    );
  }

  if (!activePatient) return <div className="text-[#576A62]">Select a patient to continue.</div>;

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <InvitesBanner />
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#576A62] font-semibold">Dashboard</div>
          <h1 className="mt-2 font-heading text-3xl sm:text-4xl font-bold">
            Caring for <span className="text-[#2C5545]">{activePatient.name}</span>
            {activePatient.is_shared && (
              <span className="ml-3 text-sm font-normal uppercase tracking-widest bg-[#3E7B5C]/15 text-[#3E7B5C] rounded-full px-3 py-1 align-middle" data-testid="dashboard-shared-badge">
                shared · {activePatient.role}
              </span>
            )}
          </h1>
          <p className="mt-2 text-[#576A62]">
            {activePatient.relationship ? `Relationship: ${activePatient.relationship}` : ""}
            {activePatient.date_of_birth ? ` · DOB: ${activePatient.date_of_birth}` : ""}
            {activePatient.is_shared && activePatient.shared_by ? ` · shared by ${activePatient.shared_by}` : ""}
          </p>
        </div>
        <Link
          to={`/app/patients/${activePatient.id}/summary`}
          data-testid="dashboard-care-summary-btn"
          className="inline-flex items-center gap-2 rounded-full border border-[#2C5545] text-[#2C5545] hover:bg-[#EBE8E0] px-5 py-3 font-semibold"
        >
          <FileText weight="duotone" size={18} /> Care summary
        </Link>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard testid="stat-medications" icon={Pill} label="Active medications" value={data?.medications_count ?? "—"} />
        <StatCard testid="stat-appointments" icon={CalendarBlank} label="Upcoming (7d)" value={data?.upcoming_appointments?.length ?? "—"} />
        <StatCard testid="stat-refills" icon={Warning} label="Low refills" tone="warning" value={data?.low_refill_meds?.length ?? "—"} />
        <StatCard testid="stat-reminders" icon={Bell} label="Reminders due" tone="secondary" value={data?.reminders_due?.length ?? "—"} />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Panel title="Upcoming appointments" icon={CalendarBlank} link="/app/appointments" testid="panel-appointments">
          {loading ? <Skeleton /> : (
            data?.upcoming_appointments?.length ? data.upcoming_appointments.map((a) => (
              <ListItem
                key={a.id}
                title={a.title || a.doctor_name || "Appointment"}
                subtitle={`${new Date(a.date).toLocaleString()} · ${a.location || a.doctor_name || ""}`}
                testid={`upcoming-${a.id}`}
              />
            )) : <Empty text="No upcoming appointments" />
          )}
        </Panel>

        <Panel title="Low refills" icon={Warning} link="/app/medications" testid="panel-refills">
          {loading ? <Skeleton /> : (
            data?.low_refill_meds?.length ? data.low_refill_meds.map((m) => (
              <ListItem
                key={m.id}
                title={m.name}
                subtitle={`${m.dosage || ""} · Refills remaining: ${m.refills_remaining ?? 0}`}
                testid={`refill-${m.id}`}
              />
            )) : <Empty text="All meds are stocked" />
          )}
        </Panel>

        <Panel title="Reminders due" icon={Bell} link="/app/reminders" testid="panel-reminders">
          {loading ? <Skeleton /> : (
            data?.reminders_due?.length ? data.reminders_due.map((r) => (
              <ListItem
                key={r.id}
                title={r.title}
                subtitle={r.due_at ? new Date(r.due_at).toLocaleString() : ""}
                testid={`reminder-${r.id}`}
              />
            )) : <Empty text="Nothing due today. Take a breath." />
          )}
        </Panel>

        <Panel title="Open questions to ask" icon={Question} link="/app/appointments" testid="panel-questions">
          {loading ? <Skeleton /> : (
            data?.open_questions?.length ? data.open_questions.map((q) => (
              <ListItem
                key={q.id}
                title={q.text}
                subtitle={q.for_doctor ? `For: ${q.for_doctor}` : ""}
                testid={`question-${q.id}`}
              />
            )) : <Empty text="No questions saved" />
          )}
        </Panel>

        <Panel title="To-dos" icon={CheckSquare} link="/app/todos" testid="panel-todos">
          {loading ? <Skeleton /> : (
            data?.open_todos?.length ? data.open_todos.slice(0, 6).map((t) => (
              <ListItem
                key={t.id}
                title={t.title}
                subtitle={t.notes || ""}
                testid={`todo-${t.id}`}
              />
            )) : <Empty text="Nothing on the list" />
          )}
        </Panel>

        <Panel title="Recent activity" icon={ClockCounterClockwise} link="/app/timeline" testid="panel-timeline">
          <TimelineFeed patientId={activePatient.id} limit={8} compact />
        </Panel>
      </div>
    </div>
  );
}

function Panel({ title, icon: Icon, link, children, testid }) {
  return (
    <div className="bg-white rounded-2xl border border-[#EBE8E0] p-6" data-testid={testid}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Icon weight="duotone" size={20} className="text-[#2C5545]" />
          <h3 className="font-heading font-bold text-lg">{title}</h3>
        </div>
        {link && (
          <Link to={link} className="text-sm text-[#2C5545] font-semibold hover:underline">
            View all →
          </Link>
        )}
      </div>
      <div className="space-y-2 max-h-72 overflow-y-auto pr-1">{children}</div>
    </div>
  );
}

function ListItem({ title, subtitle, testid }) {
  return (
    <div className="rounded-xl px-4 py-3 hover:bg-[#FDFBF7] border border-transparent hover:border-[#EBE8E0] transition-colors" data-testid={testid}>
      <div className="font-medium text-[#1A2520]">{title}</div>
      {subtitle && <div className="text-sm text-[#576A62] mt-0.5">{subtitle}</div>}
    </div>
  );
}

function Empty({ text }) {
  return <div className="text-sm text-[#576A62] py-2">{text}</div>;
}

function Skeleton() {
  return (
    <div className="space-y-2">
      {[0,1,2].map((i) => <div key={i} className="h-14 rounded-xl bg-[#EBE8E0]/50 animate-pulse" />)}
    </div>
  );
}
